/* CUSTOM_BLOG_CTA_BY_CLAUDE — Mid-article lead CTA web component.
 *
 * Responsibilities (the SUBMIT itself is NOT handled here — the popup is a real
 * {% form 'contact' %} that lead-capture-interceptor.js intercepts, so payload,
 * UTM/journey + GA attribution, Turnstile, retry queue and territory routing to
 * the Deal Dashboard are all the shared pipeline):
 *   1. Relocate the CTA to sit after the Nth top-level paragraph of the article body.
 *   2. Two-step UX: card email field -> open popup (email pre-filled) -> submit.
 *   3. Zip -> City/State lookup (mirrors contact-form.liquid) so the lead routes by territory.
 *   4. Flag the form as inline so the interceptor stays on-page, then swap the card
 *      for the "we'll be in touch" confirmation on the bsfs:lead-success event.
 */
(function () {
  'use strict';

  if (customElements.get('blog-lead-cta')) return;

  class BlogLeadCta extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      // Resolve seasonal tokens, place it, then reveal (avoids a flash + token flicker).
      this._applyTokens();
      this._placeInArticle();
      this.removeAttribute('hidden');

      if (this.dataset.mode === 'form') {
        this._wireForm();
      }
    }

    /* Move the CTA to just BEFORE the Nth pill header (<h2>) in the article body.
       Pill headers are the navy rounded section headers. We query at ANY depth
       (not just direct children) because older imported posts wrap the body in an
       extra <div class="blog-content">, which would otherwise hide every heading
       from a direct-children scan and dump the card at the bottom. The TOC heading
       is excluded via .closest('.table-of-contents'). insertAdjacentElement drops
       the card as a sibling of the anchor inside whatever wrapper it lives in, so
       this is correct for both the flat (new) and nested (old) layouts.
       Fallbacks: too few headers -> before the last one; no headers -> after the
       2nd paragraph; nothing -> leave it where the snippet rendered it. */
    _placeInArticle() {
      try {
        var content = document.querySelector('.article__content');
        if (!content) return;

        var n = parseInt(this.dataset.beforeHeader, 10);
        if (isNaN(n) || n < 1) n = 2;

        var notInToc = function (el) { return !el.closest('.table-of-contents'); };
        var heads = Array.prototype.slice.call(content.querySelectorAll('h2')).filter(notInToc);

        if (heads.length >= 1) {
          var anchor = heads[Math.min(n, heads.length) - 1];
          anchor.insertAdjacentElement('beforebegin', this);
          return;
        }

        var paras = Array.prototype.slice.call(content.querySelectorAll('p')).filter(function (p) {
          return (p.textContent || '').trim().length > 0 && notInToc(p);
        });
        if (paras.length >= 1) {
          paras[Math.min(2, paras.length) - 1].insertAdjacentElement('afterend', this);
        }
      } catch (e) { /* non-fatal — CTA simply stays where the snippet rendered it */ }
    }

    /* Replace {season} / {year} tokens in the visible copy so urgency lines like
       "Order yours this {season} while stocks last" stay current on their own. */
    _applyTokens() {
      try {
        var season = this._currentSeason();
        var year = String(new Date().getFullYear());
        var self = this;
        ['.blog-cta__headline', '.blog-cta__subtext', '.blog-cta__panel-title', '.blog-cta__panel-sub', '.blog-cta__done-msg']
          .forEach(function (sel) {
            self.querySelectorAll(sel).forEach(function (el) {
              if (el.textContent.indexOf('{') === -1) return;
              el.textContent = el.textContent.replace(/\{season\}/gi, season).replace(/\{year\}/gi, year);
            });
          });
      } catch (e) {}
    }

    _currentSeason() {
      var m = new Date().getMonth(); // 0-11 (Northern Hemisphere)
      if (m === 11 || m === 0 || m === 1) return 'winter';
      if (m >= 2 && m <= 4) return 'spring';
      if (m >= 5 && m <= 7) return 'summer';
      return 'fall';
    }

    _wireForm() {
      var self = this;
      this.cardEl = this.querySelector('[data-bsfs-cta-card]');
      this.doneEl = this.querySelector('[data-bsfs-cta-done]');
      this.modalEl = this.querySelector('[data-bsfs-cta-modal]');
      this.teaserEl = this.querySelector('[data-bsfs-cta-teaser]');
      this.teaserEmail = this.querySelector('.blog-cta__email');
      this.formEl = this.querySelector('.blog-cta__form');
      this.modalEmail = this.querySelector('[data-bsfs-cta-email]');

      if (!this.formEl) return;

      // Opt into the interceptor's inline mode (stay on page + fire success event).
      this.formEl.dataset.bsfsInline = 'true';

      // Step 1 -> open popup, carry the email across.
      if (this.teaserEl) {
        this.teaserEl.addEventListener('submit', function (e) {
          e.preventDefault();
          if (self.teaserEmail && !self.teaserEmail.checkValidity()) {
            self.teaserEmail.reportValidity();
            return;
          }
          if (self.modalEmail && self.teaserEmail) self.modalEmail.value = self.teaserEmail.value;
          self._openModal();
        });
      }

      // Close controls.
      this.querySelectorAll('[data-bsfs-cta-close]').forEach(function (el) {
        el.addEventListener('click', function () { self._closeModal(); });
      });
      this._onKeydown = function (e) { if (e.key === 'Escape') self._closeModal(); };

      this._wireZipLookup();
      this._wireCustomerType();
      this._wirePhoneFormat();

      // Inline success — the interceptor fires this on the form instead of navigating.
      this.formEl.addEventListener('bsfs:lead-success', function () {
        self._closeModal();
        if (self.cardEl) self.cardEl.hidden = true;
        if (self.doneEl) {
          self.doneEl.hidden = false;
          try { self.doneEl.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
        }
      });
    }

    _openModal() {
      if (!this.modalEl) return;
      this.modalEl.hidden = false;
      document.body.classList.add('blog-cta-modal-open');
      document.addEventListener('keydown', this._onKeydown);
      var focusTarget = this.modalEmail && this.modalEmail.value
        ? this.querySelector('[name="contact[Name]"]')
        : this.modalEmail;
      if (focusTarget) { try { focusTarget.focus(); } catch (e) {} }
    }

    _closeModal() {
      if (!this.modalEl) return;
      this.modalEl.hidden = true;
      document.body.classList.remove('blog-cta-modal-open');
      document.removeEventListener('keydown', this._onKeydown);
    }

    /* Don't presume Commercial. Organization is only relevant (and required) when
       they pick Commercial; a home buyer (e.g. a firefighter buying for the house)
       has no organization, so we hide it for Residential and don't require it until
       a Commercial selection is made. */
    _wireCustomerType() {
      var sel = this.querySelector('[data-bsfs-cta-type]');
      var field = this.querySelector('[data-bsfs-cta-org-field]');
      var input = this.querySelector('[data-bsfs-cta-org]');
      var req = this.querySelector('[data-bsfs-cta-org-req]'); // the "*" — only when required
      if (!sel || !field || !input) return;
      var sync = function () {
        var v = (sel.value || '').toLowerCase();
        var commercial = v === 'commercial';
        if (v === 'residential') { field.hidden = true; input.required = false; input.value = ''; }
        else { field.hidden = false; input.required = commercial; }
        if (req) req.hidden = !commercial;
      };
      sel.addEventListener('change', sync);
      sync();
    }

    /* Auto-format the phone as the user types. Mirrors the site-wide
       contact-form.liquid formatter exactly: (XXX) XXX-XXXX, with +1 and
       leading-1 handling, so the blog form matches every other site form. */
    _wirePhoneFormat() {
      var input = this.querySelector('[data-bsfs-cta-phone]');
      if (!input) return;
      input.addEventListener('input', function (e) {
        var value = e.target.value.replace(/[^\d+]/g, '');
        if (value.charAt(0) === '+') {
          var digits = value.substring(1).replace(/\D/g, '');
          if (digits.length === 0) value = '+';
          else if (digits.charAt(0) === '1') {
            if (digits.length <= 1) value = '+' + digits;
            else if (digits.length <= 4) value = '+1 (' + digits.substring(1);
            else if (digits.length <= 7) value = '+1 (' + digits.substring(1, 4) + ') ' + digits.substring(4);
            else value = '+1 (' + digits.substring(1, 4) + ') ' + digits.substring(4, 7) + '-' + digits.substring(7, 11);
          } else {
            value = '+' + digits;
          }
        } else if (value.charAt(0) === '1' && value.length > 1) {
          if (value.length <= 4) value = '1 (' + value.substring(1);
          else if (value.length <= 7) value = '1 (' + value.substring(1, 4) + ') ' + value.substring(4);
          else value = '1 (' + value.substring(1, 4) + ') ' + value.substring(4, 7) + '-' + value.substring(7, 11);
        } else if (value.length > 0) {
          if (value.length <= 3) value = '(' + value;
          else if (value.length <= 6) value = '(' + value.substring(0, 3) + ') ' + value.substring(3);
          else value = '(' + value.substring(0, 3) + ') ' + value.substring(3, 6) + '-' + value.substring(6, 10);
        }
        e.target.value = value;
      });
    }

    /* Zip -> "City, ST ZIP" into contact[location] (interceptor parses out state for routing). */
    _wireZipLookup() {
      var zip = this.querySelector('[data-bsfs-cta-zip]');
      var location = this.querySelector('[data-bsfs-cta-location]');
      var status = this.querySelector('[data-bsfs-cta-zip-status]');
      if (!zip || !location) return;
      var timer;

      zip.addEventListener('input', function () {
        var val = (zip.value || '').replace(/\D/g, '').slice(0, 5);
        zip.value = val;
        clearTimeout(timer);
        if (val.length < 5) {
          location.value = '';
          if (status) { status.textContent = ''; status.className = 'blog-cta__zip-status'; }
          return;
        }
        timer = setTimeout(function () {
          if (status) { status.textContent = 'Looking up...'; status.className = 'blog-cta__zip-status'; }
          fetch('https://api.zippopotam.us/us/' + val)
            .then(function (r) { if (!r.ok) throw new Error('not found'); return r.json(); })
            .then(function (data) {
              var city = data.places[0]['place name'];
              var state = data.places[0]['state abbreviation'];
              location.value = city + ', ' + state + ' ' + val;
              if (status) { status.textContent = '✓ ' + city + ', ' + state; status.className = 'blog-cta__zip-status is-success'; }
            })
            .catch(function () {
              location.value = 'Unknown - ' + val;
              if (status) { status.textContent = 'Zip not found - please verify'; status.className = 'blog-cta__zip-status is-error'; }
            });
        }, 400);
      });
    }
  }

  customElements.define('blog-lead-cta', BlogLeadCta);
})();
